import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import ProposalFieldSchema from "work/proposals/proposal-fields/_schema/proposal-field-schema";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { z } from "zod";

export type CommentThreadJSON = z.infer<typeof CommentThreadSchema.CommentThreadJSONZ>;
export type CommentThreadParams = z.infer<typeof CommentThreadSchema.CommentThreadZ>;

export default class CommentThreadSchema {
  public static readonly CommentThreadJSONZ = z.object({
    id: z.uuid().nullish(),
    proposalId: z.uuid(),
    field: ProposalFieldSchema.ProposalFieldJSONZ,
    subscribers: z.array(EntityMemberSchema.EntityMemberJSONZ).nullish(),
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    isExternal: z.boolean(),
    isResolved: z.boolean().nullish(),
    numUnreadMessages: z.number().nullish(),
  });

  public static readonly CommentThreadZ = z.object({
    id: z.instanceof(Guid).optional(),
    proposalId: z.instanceof(Guid),
    field: z.instanceof(ProposalField),
    isExternal: z.boolean(),
    subscribers: z.array(z.instanceof(EntityMember)),
    createdDate: z.instanceof(AHDate).optional(),
    isResolved: z.boolean().optional(),
    numUnreadMessages: z.number().optional(),
  });

  public static validate(values: unknown): CommentThreadParams {
    return parseOrThrow(CommentThreadSchema.CommentThreadZ, values, {
      payloadSample: values,
      schemaName: "CommentThreadSchema",
      operation: "validate",
      logError: true,
    });
  }

  public static validateJSON(json: unknown): CommentThreadJSON {
    return parseOrThrow(CommentThreadSchema.CommentThreadJSONZ, json, {
      payloadSample: json,
      schemaName: "CommentThreadSchema",
      operation: "validateJSON",
      logError: true,
    });
  }

  public static validateJSONArray(jsonArray: unknown[]): CommentThreadJSON[] {
    return jsonArray.map((json) => CommentThreadSchema.validateJSON(json));
  }

  public static isValid(values: unknown): values is CommentThreadParams {
    try {
      CommentThreadSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is CommentThreadJSON {
    try {
      CommentThreadSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: CommentThreadJSON): CommentThreadParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(CommentThreadSchema.CommentThreadJSONZ, json, {
      payloadSample: json,
      schemaName: "CommentThreadSchema",
      operation: "deserialize",
      logError: true,
    });
    return CommentThreadSchema.codec.decode(parsed);
  }

  public static serialize(params: CommentThreadParams): CommentThreadJSON {
    const parsed = CommentThreadSchema.validate(params);
    return CommentThreadSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(CommentThreadSchema.CommentThreadJSONZ, CommentThreadSchema.CommentThreadZ, {
    decode: (json) => ({
      id: Guid.fromJSON(json.id) ?? undefined,
      proposalId: new Guid(json.proposalId),
      field: ProposalField.fromJSON(json.field)!,
      isExternal: json.isExternal,
      subscribers: EntityMember.fromJSONArray({ jsonArray: json.subscribers }) ?? [],
      createdDate: AHDate.fromJSON(json.createdDate) ?? undefined,
      isResolved: json.isResolved ?? false,
      numUnreadMessages: json.numUnreadMessages ?? 0,
    }),
    encode: (params) => ({
      id: params.id?.toJSON(),
      proposalId: params.proposalId.toJSON(),
      field: params.field.toJSON(),
      subscribers: params.subscribers?.map((subscriber) => subscriber.toJSON()) ?? [],
      createdDate: params.createdDate ? params.createdDate.toJSON() : undefined,
      isResolved: params.isResolved,
      numUnreadMessages: params.numUnreadMessages,
      isExternal: params.isExternal,
    }),
  });
}
